import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';

interface DealerCredit {
  dealerId: string;
  companyName: string;
  availableCredit: number;
  lastActivity: string | null;
}

interface Overpayment {
  orderId: string;
  orderNumber: string;
  dealerId: string;
  companyName: string;
  sidemark: string | null;
  total: number;
  paid: number;
  overpaidAmount: number;
  paymentDate: string | null;
}

const money = (n: any) =>
  '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const REFUND_METHODS = [
  { value: 'credit_to_account', label: "Credit to dealer's account (no money out)" },
  { value: 'check', label: 'Check (cut & mailed)' },
  { value: 'ach', label: 'ACH (sent back)' },
  { value: 'cash', label: 'Cash' },
  { value: 'wire', label: 'Wire transfer' },
  { value: 'other', label: 'Other' },
];

export default function CreditsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<'credits' | 'overpayments'>(
    searchParams.get('tab') === 'overpayments' ? 'overpayments' : 'credits',
  );
  const [credits, setCredits] = useState<DealerCredit[]>([]);
  const [overpayments, setOverpayments] = useState<Overpayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Resolve-overpayment modal (issue credit / refund via order refund)
  const [resolveRow, setResolveRow] = useState<Overpayment | null>(null);
  const [resolveForm, setResolveForm] = useState({ amount: '', method: 'credit_to_account', reference: '', reason: '' });

  // Adjust-credit modal
  const [adjustRow, setAdjustRow] = useState<DealerCredit | null>(null);
  const [adjustForm, setAdjustForm] = useState({ direction: 'add' as 'add' | 'remove', amount: '', description: '' });

  // Apply-credit-to-order modal
  const [applyRow, setApplyRow] = useState<DealerCredit | null>(null);
  const [applyOrders, setApplyOrders] = useState<Array<{ id: string; orderNumber: string; balanceDue: number }>>([]);
  const [applyForm, setApplyForm] = useState({ orderId: '', amount: '' });
  const [applyLoadingOrders, setApplyLoadingOrders] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, o] = await Promise.all([
        api.accounting.getDealerCredits(),
        api.accounting.getOpenOverpayments(),
      ]);
      setCredits(c || []);
      setOverpayments(o || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchTab = (t: 'credits' | 'overpayments') => {
    setTab(t);
    setSearchParams(t === 'overpayments' ? { tab: 'overpayments' } : {});
  };

  const creditTotal = credits.reduce((s, r) => s + (Number(r.availableCredit) || 0), 0);
  const overpayTotal = overpayments.reduce((s, r) => s + (Number(r.overpaidAmount) || 0), 0);

  // ---- Resolve overpayment (issue credit / refund) ----
  const openResolve = (row: Overpayment, method: string) => {
    setResolveRow(row);
    setResolveForm({ amount: row.overpaidAmount.toFixed(2), method, reference: '', reason: `Overpayment on ${row.orderNumber}` });
    setNotice(null);
  };
  const submitResolve = async () => {
    if (!resolveRow) return;
    const amount = parseFloat(resolveForm.amount);
    if (!amount || amount <= 0) { setNotice('Enter a valid amount.'); return; }
    setSubmitting(true);
    try {
      await api.orders.issueRefund(resolveRow.orderId, {
        amount,
        method: resolveForm.method as any,
        reference: resolveForm.reference || undefined,
        reason: resolveForm.reason || undefined,
      });
      setNotice(
        resolveForm.method === 'credit_to_account'
          ? `Issued ${money(amount)} store credit for ${resolveRow.orderNumber}.`
          : `Refunded ${money(amount)} on ${resolveRow.orderNumber}.`,
      );
      setResolveRow(null);
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Failed to resolve overpayment');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Manual adjust ----
  const openAdjust = (row: DealerCredit) => {
    setAdjustRow(row);
    setAdjustForm({ direction: 'add', amount: '', description: '' });
    setNotice(null);
  };
  const submitAdjust = async () => {
    if (!adjustRow) return;
    const amt = parseFloat(adjustForm.amount);
    if (!amt || amt <= 0) { setNotice('Enter a positive amount.'); return; }
    if (!adjustForm.description.trim()) { setNotice('A note/description is required.'); return; }
    // Ledger convention: credit owed = NEGATIVE balance. Add credit → subtract
    // from balance (negative signed amount); Remove credit → positive.
    const signed = adjustForm.direction === 'add' ? -amt : amt;
    setSubmitting(true);
    try {
      await api.accounting.adjustDealerCredit(adjustRow.dealerId, signed, adjustForm.description.trim());
      setNotice(`${adjustForm.direction === 'add' ? 'Added' : 'Removed'} ${money(amt)} ${adjustForm.direction === 'add' ? 'to' : 'from'} ${adjustRow.companyName}'s credit.`);
      setAdjustRow(null);
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Failed to adjust credit');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Apply credit to an open order ----
  const openApply = async (row: DealerCredit) => {
    setApplyRow(row);
    setApplyForm({ orderId: '', amount: '' });
    setApplyOrders([]);
    setNotice(null);
    setApplyLoadingOrders(true);
    try {
      const res: any = await api.dealers.getDealerOrders(row.dealerId, { pageSize: 100 } as any);
      const list = res?.data || res?.orders || res || [];
      const withBalance = (Array.isArray(list) ? list : [])
        .map((o: any) => {
          const total = Number(o.totalAmount ?? o.total_amount ?? 0);
          const paid = Number(o.paymentAmount ?? o.payment_amount ?? 0);
          const refunded = Number(o.refundedAmount ?? o.refunded_amount ?? 0);
          const ach = Number(o.achDiscountAmount ?? o.ach_discount_amount ?? 0);
          const balanceDue = Math.round((total - (paid - refunded + ach)) * 100) / 100;
          return { id: o.id, orderNumber: o.orderNumber ?? o.order_number, status: o.status, balanceDue };
        })
        .filter((o: any) => o.balanceDue > 0.01 && !['draft', 'cancelled'].includes(o.status))
        .sort((a: any, b: any) => b.balanceDue - a.balanceDue);
      setApplyOrders(withBalance);
    } catch (e: any) {
      setNotice(e?.message || 'Failed to load dealer orders');
    } finally {
      setApplyLoadingOrders(false);
    }
  };
  const onPickApplyOrder = (orderId: string) => {
    const ord = applyOrders.find((o) => o.id === orderId);
    const suggested = ord ? Math.min(applyRow?.availableCredit || 0, ord.balanceDue) : 0;
    setApplyForm({ orderId, amount: suggested ? suggested.toFixed(2) : '' });
  };
  const submitApply = async () => {
    if (!applyRow || !applyForm.orderId) { setNotice('Pick an order.'); return; }
    const amount = parseFloat(applyForm.amount);
    if (!amount || amount <= 0) { setNotice('Enter a valid amount.'); return; }
    setSubmitting(true);
    try {
      await api.accounting.applyCreditToOrder(applyRow.dealerId, applyForm.orderId, amount);
      setNotice(`Applied ${money(amount)} credit to the order.`);
      setApplyRow(null);
      load();
    } catch (e: any) {
      setNotice(e?.message || 'Failed to apply credit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dealer Credits &amp; Overpayments</h1>
        <p className="text-gray-500 mt-1">Money owed to dealers — store credit balances and orders paid over total. Actions are manual; nothing is auto-issued.</p>
      </div>

      {notice && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 text-sm flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="font-bold">×</button>
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-l-emerald-500">
          <p className="text-sm text-gray-500">Available Store Credit</p>
          <p className="text-2xl font-bold text-emerald-600">{money(creditTotal)}</p>
          <p className="text-xs text-gray-400">{credits.length} dealer{credits.length === 1 ? '' : 's'}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5 border-l-4 border-l-purple-500">
          <p className="text-sm text-gray-500">Open Overpayments</p>
          <p className="text-2xl font-bold text-purple-600">{money(overpayTotal)}</p>
          <p className="text-xs text-gray-400">{overpayments.length} order{overpayments.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => switchTab('credits')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'credits' ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Store Credits ({credits.length})
        </button>
        <button
          onClick={() => switchTab('overpayments')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'overpayments' ? 'border-purple-500 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Open Overpayments ({overpayments.length})
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm py-10 text-center">Loading…</p>
      ) : tab === 'credits' ? (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-3 px-4">Dealer</th>
                <th className="py-3 px-4 text-right">Available Credit</th>
                <th className="py-3 px-4">Last Activity</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {credits.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-gray-400">No dealers with a store-credit balance.</td></tr>
              )}
              {credits.map((r) => (
                <tr key={r.dealerId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    <Link to={`/dealers/${r.dealerId}`} className="hover:underline">{r.companyName}</Link>
                  </td>
                  <td className="py-3 px-4 text-right font-semibold text-emerald-600">{money(r.availableCredit)}</td>
                  <td className="py-3 px-4 text-gray-500">{r.lastActivity ? new Date(r.lastActivity).toLocaleDateString() : '—'}</td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openApply(r)} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 mr-2">Apply to order</button>
                    <button onClick={() => openAdjust(r)} className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-100">Adjust</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-3 px-4">Order</th>
                <th className="py-3 px-4">Dealer</th>
                <th className="py-3 px-4 text-right">Total</th>
                <th className="py-3 px-4 text-right">Paid</th>
                <th className="py-3 px-4 text-right">Overpaid</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {overpayments.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-gray-400">No open overpayments.</td></tr>
              )}
              {overpayments.map((r) => (
                <tr key={r.orderId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">
                    <Link to={`/orders/${r.orderId}`} className="text-blue-600 hover:underline">{r.orderNumber}</Link>
                    {r.sidemark && <span className="block text-xs text-gray-400">{r.sidemark}</span>}
                  </td>
                  <td className="py-3 px-4 text-gray-700">{r.companyName}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{money(r.total)}</td>
                  <td className="py-3 px-4 text-right text-gray-600">{money(r.paid)}</td>
                  <td className="py-3 px-4 text-right font-semibold text-purple-600">{money(r.overpaidAmount)}</td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => openResolve(r, 'credit_to_account')} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 mr-2">Issue credit</button>
                    <button onClick={() => openResolve(r, 'check')} className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-100">Refund</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Resolve overpayment modal */}
      {resolveRow && (
        <Modal title={`Resolve overpayment — ${resolveRow.orderNumber}`} onClose={() => setResolveRow(null)}>
          <p className="text-sm text-gray-500 mb-4">{resolveRow.companyName} overpaid {money(resolveRow.overpaidAmount)} on this order.</p>
          <Field label="Amount">
            <input type="number" step="0.01" min="0" value={resolveForm.amount}
              onChange={(e) => setResolveForm(f => ({ ...f, amount: e.target.value }))}
              className="input" />
          </Field>
          <Field label="Method">
            <select value={resolveForm.method} onChange={(e) => setResolveForm(f => ({ ...f, method: e.target.value }))} className="input">
              {REFUND_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          <Field label="Reference">
            <input value={resolveForm.reference} onChange={(e) => setResolveForm(f => ({ ...f, reference: e.target.value }))} className="input" placeholder="Check #, confirmation, etc." />
          </Field>
          <Field label="Reason / note">
            <input value={resolveForm.reason} onChange={(e) => setResolveForm(f => ({ ...f, reason: e.target.value }))} className="input" />
          </Field>
          <ModalActions onCancel={() => setResolveRow(null)} onSubmit={submitResolve} submitting={submitting}
            submitLabel={resolveForm.method === 'credit_to_account' ? 'Issue credit' : 'Record refund'} />
        </Modal>
      )}

      {/* Adjust credit modal */}
      {adjustRow && (
        <Modal title={`Adjust credit — ${adjustRow.companyName}`} onClose={() => setAdjustRow(null)}>
          <p className="text-sm text-gray-500 mb-4">Current available credit: <strong>{money(adjustRow.availableCredit)}</strong></p>
          <Field label="Direction">
            <select value={adjustForm.direction} onChange={(e) => setAdjustForm(f => ({ ...f, direction: e.target.value as any }))} className="input">
              <option value="add">Add credit (we owe more)</option>
              <option value="remove">Remove credit (paid out / correction)</option>
            </select>
          </Field>
          <Field label="Amount">
            <input type="number" step="0.01" min="0" value={adjustForm.amount}
              onChange={(e) => setAdjustForm(f => ({ ...f, amount: e.target.value }))} className="input" />
          </Field>
          <Field label="Note (required)">
            <input value={adjustForm.description} onChange={(e) => setAdjustForm(f => ({ ...f, description: e.target.value }))} className="input" placeholder="Reason for adjustment" />
          </Field>
          <ModalActions onCancel={() => setAdjustRow(null)} onSubmit={submitAdjust} submitting={submitting} submitLabel="Save adjustment" />
        </Modal>
      )}

      {/* Apply credit to order modal */}
      {applyRow && (
        <Modal title={`Apply credit — ${applyRow.companyName}`} onClose={() => setApplyRow(null)}>
          <p className="text-sm text-gray-500 mb-4">Available credit: <strong>{money(applyRow.availableCredit)}</strong></p>
          {applyLoadingOrders ? (
            <p className="text-sm text-gray-400">Loading open orders…</p>
          ) : applyOrders.length === 0 ? (
            <p className="text-sm text-gray-500">This dealer has no open orders with a balance due.</p>
          ) : (
            <>
              <Field label="Order">
                <select value={applyForm.orderId} onChange={(e) => onPickApplyOrder(e.target.value)} className="input">
                  <option value="">Select an order…</option>
                  {applyOrders.map(o => (
                    <option key={o.id} value={o.id}>{o.orderNumber} — balance {money(o.balanceDue)}</option>
                  ))}
                </select>
              </Field>
              <Field label="Amount to apply">
                <input type="number" step="0.01" min="0" value={applyForm.amount}
                  onChange={(e) => setApplyForm(f => ({ ...f, amount: e.target.value }))} className="input" />
              </Field>
            </>
          )}
          <ModalActions onCancel={() => setApplyRow(null)} onSubmit={submitApply} submitting={submitting}
            submitLabel="Apply credit" disabled={!applyForm.orderId} />
        </Modal>
      )}
    </div>
  );
}

// ---- small local UI helpers ----
function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ onCancel, onSubmit, submitting, submitLabel, disabled }: {
  onCancel: () => void; onSubmit: () => void; submitting: boolean; submitLabel: string; disabled?: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 mt-5">
      <button onClick={onCancel} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
      <button onClick={onSubmit} disabled={submitting || disabled}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}
